'use client';

import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { usePatchRawProduct } from '../hooks/useRawProducts';
import { useQueryClient } from '@tanstack/react-query';
import { useNewChat } from './NewChatContext';
import { useRouter } from 'next/navigation';
import { accordionKeys } from '@/app/config/tabConfig';

interface MultiProductsLifeStyleImageGenContextProps {
    botApiHandler: (body: any, signals: any) => Promise<void>;
    initChat: (images: Record<string, Array<string>>) => Promise<void>;
    selectedImages: Record<string, Array<string>>;
    setSelectedImages: React.Dispatch<React.SetStateAction<Record<string, Array<string>>>>;
    showLifeStyleImageZoomModal: boolean;
    selectedLifeStyleImageUrlForZoomModal: string;
    handleShowLifeStyleImageZoomModal: (val: boolean) => void;
    handleSelectedLifeStyleImageUrlForZoomModal: (url: string) => void;
    isMultiProductLifeStyleImageGenerating: boolean;
    setIsMultiProductLifeStyleImageGenerating: React.Dispatch<React.SetStateAction<boolean>>;
}

const MultiProductsLifeStyleImageGenContext = createContext<MultiProductsLifeStyleImageGenContextProps | undefined>(undefined);

export const MultiProductsLifeStyleImageGenProvider = ({ children }: { children: React.ReactNode }) => {
    const { mutate: patchRawProduct } = usePatchRawProduct()
    const queryClient = useQueryClient();
    const router = useRouter();
    const { openChat, appendToHistory } = useNewChat();
    const [selectedImages, setSelectedImages] = useState<Record<string, Array<string>>>({});
    const [isMultiProductLifeStyleImageGenerating, setIsMultiProductLifeStyleImageGenerating] = useState(false);
    const sessionIdRef = useRef<string>('');
    //state for Modal view of the image
    const [showLifeStyleImageZoomModal, setShowLifeStyleImageZoomModal] = useState(false)
    const [selectedLifeStyleImageUrlForZoomModal, setSelectedLifeStyleImageUrlForZoomModal] = useState("");
    //handlers for zoom modal

    function handleShowLifeStyleImageZoomModal(val: boolean) {
        setShowLifeStyleImageZoomModal(val)
    }

    function handleSelectedLifeStyleImageUrlForZoomModal(url: string) {
        setSelectedLifeStyleImageUrlForZoomModal(url)
    }
    useEffect(() => {
        if (!openChat) {
            sessionIdRef.current = ''
        } else if (!sessionIdRef.current) {
            const generateSessionId = () => {
                const timestamp = Date.now();
                return `session_${timestamp}`;
            };
            const newSessionId = generateSessionId();
            sessionIdRef.current = newSessionId;
        }
    }, [openChat]);

    const initChat = async (images: Record<string, Array<string>>) => {
        appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: "Please wait..." }]);
        let currentSessionId = sessionIdRef.current;
        if (!currentSessionId) {
            currentSessionId = `session_${Date.now()}`;
            sessionIdRef.current = currentSessionId;
        }

        const imagesPayload = Object.entries(images).map(([sku, urls]) => ({
            [sku]: urls
        }));

        const payload = {
            sessionId: currentSessionId,
            images: imagesPayload,
            prompt: "hi",
            genFlag: ""
        };

        try {
            const response = await fetch("/ai/api/multi-silo-room-scene-gen", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error("Failed to initialize session");
            }

            const data = await response.json();
            // const data = json.data;

            // Only add the AI response to the UI
            if (data.text) {
                appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: data.text }]);
            }
        } catch (error) {
            console.error("Init Chat API Error", error);
            appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: "❌ Failed to initialize chat." }]);
        }
    };

    const botApiHandler = async (body: any, signals: any) => {
        const userMessage = body.messages[body.messages.length - 1].text;
        appendToHistory("multiProductLifestyleImageGen", [{ role: "user", text: userMessage }]);
        const imagesPayload = Object.entries(selectedImages).map(([sku, urls]) => ({
            [sku]: urls
        }));

        const payload = {
            sessionId: sessionIdRef.current,
            images: imagesPayload,
            prompt: userMessage,
            genFlag: ""
        };

        try {
            const response = await fetch("/ai/api/multi-silo-room-scene-gen", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error("Failed to process request");
            }

            const data = await response.json();
            // const data = json.data;

            if (data.status === "RoutingForImageGen" || data.status === "RoutingForEdit") {
                appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: data.text }]);
                setIsMultiProductLifeStyleImageGenerating(true);

                const skus = Object.keys(selectedImages);
                if (skus.length > 0) {
                    router.push(`/supplier/products/${skus[0]}?tab=${accordionKeys.images}&child=${accordionKeys.lifestyle}`);
                }

                const secondPayload = {
                    ...payload,
                    genFlag: data.status
                };

                const secondResponse = await fetch("/ai/api/multi-silo-room-scene-gen", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(secondPayload)
                });

                if (!secondResponse.ok) {
                    throw new Error("Failed during generation/edit phase");
                }

                const secondData = await secondResponse.json();
                // const secondData = secondJson.data;

                if (secondData.genImgUrl) {
                    const responseHtml = `<div><p>${secondData.text || ''}</p><img src="${secondData.genImgUrl}" alt="Generated Image" style="max-width: 100%; border-radius: 8px; margin-top: 4px;" /></div>`;
                    // signals.onResponse({ html: responseHtml });

                    appendToHistory("multiProductLifestyleImageGen", [{
                        role: "ai",
                        text: secondData.text || "Image successfully generated, but no URL was returned.",
                        files: [{ src: secondData.genImgUrl, type: "image" }],
                    }]);
                    if (secondData.skus && Array.isArray(secondData.skus)) {
                        const queriesData = queryClient.getQueriesData<any>({ queryKey: ['rawProducts'] });

                        secondData.skus.forEach((sku: string) => {
                            let foundProduct: any = null;

                            // 1. Try rawProducts list cache
                            for (const [key, data] of queriesData) {
                                const productsArray = Array.isArray(data) ? data : (data?.data || []);
                                const product = productsArray.find((p: any) => p.productSkuId === sku);
                                if (product) {
                                    foundProduct = product;
                                    break;
                                }
                            }

                            // 2. Fallback: try rawProductById cache
                            if (!foundProduct) {
                                const productByIdData = queryClient.getQueryData<any>(['rawProductById', sku]);
                                if (Array.isArray(productByIdData) && productByIdData.length > 0) {
                                    foundProduct = productByIdData[0];
                                }
                            }

                            const existingImages = foundProduct?.lifeStyleImagesFromSilos || [];

                            const skusPayloadForImage = secondData.skus.map((s: string) => {
                                const found = imagesPayload.find((ip: Record<string, any>) => ip[s]);
                                return {
                                    productSkuId: s,
                                    imageUrl: found ? (found as Record<string, any>)[s]?.[0] || "" : ""
                                };
                            });

                            const newImageRecord = {
                                imageUrl: secondData.genImgUrl,
                                generatedAt: new Date().toISOString(),
                                skus: skusPayloadForImage
                            };

                            patchRawProduct({
                                productId: sku,
                                payload: { lifeStyleImagesFromSilos: [...existingImages, newImageRecord] }
                            });
                        });
                    }
                    setIsMultiProductLifeStyleImageGenerating(false);
                } else {
                    // signals.onResponse({ text: secondData.text || "Image successfully generated, but no URL was returned." });
                    appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: secondData.text || "Image successfully generated, but no URL was returned." }]);
                    setIsMultiProductLifeStyleImageGenerating(false);
                }
            } else {
                // Return text response for OffTopic or PendingForImageGen
                // signals.onResponse({ text: data.text });
                appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: data.text }]);
            }
        } catch (error) {
            console.error("API Error", error);
            // signals.onResponse({ text: "Sorry, an error occurred while processing your request." });
            appendToHistory("multiProductLifestyleImageGen", [{ role: "ai", text: "❌ Something went wrong while communicating with the server." }]);
            setIsMultiProductLifeStyleImageGenerating(false);
        }
    }

    return (
        <MultiProductsLifeStyleImageGenContext.Provider value={{
            botApiHandler,
            initChat,
            selectedImages,
            setSelectedImages,
            showLifeStyleImageZoomModal,
            selectedLifeStyleImageUrlForZoomModal,
            handleShowLifeStyleImageZoomModal,
            handleSelectedLifeStyleImageUrlForZoomModal,
            isMultiProductLifeStyleImageGenerating,
            setIsMultiProductLifeStyleImageGenerating,
        }}>
            {children}
        </MultiProductsLifeStyleImageGenContext.Provider>
    );
};

export const useMultiProductsLifeStyleImageGen = (): MultiProductsLifeStyleImageGenContextProps => {
    const context = useContext(MultiProductsLifeStyleImageGenContext);
    if (context === undefined) {
        throw new Error('useMultiProductsLifeStyleImageGen must be used within a MultiProductsLifeStyleImageGenProvider');
    }
    return context;
};
